<template>
  <div class="musubi-page dm-page">
    <header class="musubi-page__head">
      <div class="dm-head-row">
        <h1>
          Direct Mail - Who gets mail
          <span class="dm-badge">New</span>
        </h1>
        <span v-if="loaded && !isPaying" class="plan-pill plan-pill--free"><CygnusIcon name="info-circle" /> Free plan</span>
      </div>
      <p class="text-muted">
        Build a postal audience from your shared business corpus. Direct mail reaches the businesses your email
        campaigns legally cannot - including <strong>sole traders</strong>, who are excluded from cold email but
        perfectly targetable by post.
        <template v-if="loaded && !isPaying">
          On the free plan you can target and count the whole market - you just see a
          {{ previewLimit }}-row preview of the list.
        </template>
      </p>
    </header>

    <!-- Stepper -->
    <nav class="dm-steps" aria-label="Direct mail steps">
      <div class="dm-step is-current">
        <span class="dm-step__num">1</span>
        <span class="dm-step__body"><span class="dm-step__title">Who gets mail</span><span class="dm-step__sub">Audience</span></span>
      </div>
      <div class="dm-step is-todo">
        <span class="dm-step__num">2</span>
        <span class="dm-step__body"><span class="dm-step__title">Design your mail piece</span><span class="dm-step__sub">Format &amp; artwork</span></span>
      </div>
      <div class="dm-step is-todo">
        <span class="dm-step__num">3</span>
        <span class="dm-step__body"><span class="dm-step__title">{{ isPaying ? 'Review &amp; submit' : 'Buy this campaign' }}</span><span class="dm-step__sub">{{ isPaying ? 'Confirm' : 'One-off checkout' }}</span></span>
      </div>
    </nav>

    <div class="dm-grid">
      <!-- ── Filter panel ── -->
      <section class="dm-card dm-filters-card">
        <div class="dm-card__head"><CygnusIcon name="filter" class="dm-card__icon" /><h3 class="dm-card__title">Filters</h3></div>
        <div class="dm-card__body">

          <!-- Saved audiences -->
          <div v-if="savedList.length" class="f-row">
            <label class="f-lbl">Saved lists</label>
            <div class="dm-saved">
              <select class="f-ctrl" @change="onLoadSaved($event.target.value)">
                <option value="">Load a saved list…</option>
                <option v-for="s in savedList" :key="s.id" :value="s.id">{{ s.name }} ({{ fmtNum(s.snapshot_count) }})</option>
              </select>
              <button v-if="loadedSavedId" class="dm-icon-btn" title="Delete this saved list" @click="deleteSaved(loadedSavedId)"><CygnusIcon name="trash" /></button>
            </div>
          </div>

          <div class="filters">
            <div class="f-row">
              <label class="f-lbl">Region</label>
              <select class="f-ctrl" v-model="filters.region" @change="schedulePreview">
                <option value="">Any region</option>
                <option v-for="r in regionOptions" :key="r.code" :value="r.code">{{ r.label }}</option>
              </select>
            </div>

            <div class="f-row">
              <label class="f-lbl">Postcode area</label>
              <input class="f-ctrl" type="text" v-model="filters.postcodeAreas" placeholder="e.g. CA, LA13" @input="schedulePreview">
              <span class="f-note">Outward-code prefixes, comma separated.</span>
            </div>

            <div class="f-row">
              <label class="f-lbl">Sector</label>
              <MusubiSectorSelect v-model="filters.sectors" placeholder="Any industry" @update:modelValue="schedulePreview" />
            </div>

            <div class="f-row">
              <label class="f-lbl">Staff count band</label>
              <div class="chip-row">
                <span v-for="b in staffBands" :key="b.key" class="chip" :class="{ 'is-on': b.on }" @click="toggleBand(b)">{{ b.key }}</span>
              </div>
            </div>

            <div class="f-row">
              <label class="f-lbl">Registration status</label>
              <div class="chip-row">
                <span
                  v-for="s in statusOptions" :key="s.value"
                  class="chip" :class="{ 'is-on': filters.registrationStatuses.includes(s.value) }"
                  @click="toggleStatus(s.value)"
                >{{ s.label }}</span>
              </div>
              <span v-if="filters.registrationStatuses.includes('unchecked')" class="f-note">
                <CygnusIcon name="info-circle" /> Recently discovered - Companies House check pending. Fine for post.
              </span>
            </div>

            <!-- Highlighted sole-trader toggle -->
            <label class="toggle-card">
              <span class="toggle-card__ic"><CygnusIcon name="envelope-open-text" /></span>
              <span class="toggle-card__body">
                <span class="toggle-card__title">Include sole traders</span>
                <span class="toggle-card__desc">Post can reach businesses email cannot. Sole traders are GDPR-protected individuals - excluded from cold email, but fair game by post.</span>
              </span>
              <span class="switch">
                <input type="checkbox" v-model="filters.includeSoleTraders" @change="schedulePreview">
                <span class="slider"></span>
              </span>
            </label>

            <div class="f-row">
              <label class="f-lbl">Address quality</label>
              <div class="chip-row">
                <span class="chip" :class="{ 'is-on': filters.completeOnly }" @click="filters.completeOnly = !filters.completeOnly; schedulePreview()">
                  <CygnusIcon name="check" /> Complete postal address only
                </span>
              </div>
              <span class="f-note">Rows missing a postcode or street line are flagged and excluded from the send count.</span>
            </div>

            <div class="f-row">
              <label class="f-lbl">Growth signals</label>
              <div class="chip-row">
                <span class="chip" :class="{ 'is-on': filters.hiring }" @click="filters.hiring = !filters.hiring; schedulePreview()">
                  <CygnusIcon name="user-plus" /> Currently hiring
                </span>
              </div>
              <span class="f-note">Firms with open vacancies detected on their careers page - a growth + budget signal.</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Results ── -->
      <div class="dm-results">
        <!-- Live count chip -->
        <div class="dm-preview">
          <CygnusIcon name="users" class="dm-preview__ic" />
          <span class="dm-preview__n">{{ loading ? '…' : fmtNum(preview.total) }}</span>
          <span class="dm-preview__t">businesses match these filters<template v-if="filters.completeOnly"> and have a complete postal address</template></span>
          <span v-if="preview.excluded_incomplete > 0" class="dm-preview__excluded"><b>{{ fmtNum(preview.excluded_incomplete) }}</b> excluded<br>for incomplete address</span>
          <span v-if="preview.suppressed_count > 0" class="dm-preview__supp" title="Companies flagged do-not-mail are excluded from every audience"><CygnusIcon name="lock" /> {{ fmtNum(preview.suppressed_count) }} suppressed</span>

          <!-- Live discovery trigger -->
          <div class="dm-discover-slot">
            <button
              v-if="isPaying"
              class="btn btn-discover"
              :disabled="discovering || discoveryActive"
              title="Actively search the web for more businesses matching these filters"
              @click="findMore"
            >
              <CygnusIcon :name="discovering || discoveryActive ? 'arrows-rotate' : 'magnifying-glass'" :class="{ 'is-spin': discovering || discoveryActive }" />
              {{ discoveryActive ? "Discovering…" : "Find more businesses like this" }}
            </button>
            <router-link v-else to="/admin/usage-plans" class="dm-discover-lock" title="Live discovery is available on the Growth plan">
              <span class="dm-discover-lock__ic"><CygnusIcon name="lock" /></span>
              <span class="dm-discover-lock__txt">Grow the pool with live discovery</span>
              <span class="plan-pill plan-pill--growth"><CygnusIcon name="rocket" /> Growth plan</span>
            </router-link>
          </div>
        </div>

        <!-- Discovery status message (started / narrow filters / rate-limited) -->
        <div v-if="discoveryMsg" class="dm-discover-note" :class="'dm-discover-note--' + discoveryMsgType">
          <CygnusIcon :name="discoveryMsgType === 'info' ? 'check-circle' : 'warning'" />
          <span>{{ discoveryMsg }}</span>
        </div>

        <!-- Progress strip — polls while direct_mail discovery jobs are in flight -->
        <div v-if="discoveryActive || discovery.done > 0" class="dm-discover-strip" :class="{ 'is-done': !discoveryActive }">
          <span class="dm-discover-strip__ic"><CygnusIcon name="arrows-rotate" :class="{ 'is-spin': discoveryActive }" /></span>
          <div class="dm-discover-strip__body">
            <span class="dm-discover-strip__title">
              <template v-if="discoveryActive">Discovery running - {{ fmtNum(discovery.active) }} search{{ discovery.active === 1 ? "" : "es" }} in progress</template>
              <template v-else>Discovery finished - {{ fmtNum(discovery.done) }} search{{ discovery.done === 1 ? "" : "es" }} completed</template>
            </span>
            <span class="dm-discover-strip__sub">
              <template v-if="discoveryActive">New businesses appear here as they are found. This can take a few minutes - you can keep working.</template>
              <template v-else>The count above now includes anything new we found. Adjust your filters and search again to widen the net.</template>
            </span>
          </div>
        </div>

        <section class="dm-card dm-results-card">
          <div class="dm-card__head">
            <CygnusIcon name="list" class="dm-card__icon" />
            <h3 class="dm-card__title">Recipients</h3>
            <span class="dm-card__subtitle" v-if="!loading">
              <template v-if="preview.preview_limited">preview - {{ fmtNum(preview.rows.length) }} of {{ fmtNum(preview.total) }}</template>
              <template v-else>showing {{ fmtNum(preview.rows.length) }} of {{ fmtNum(preview.total) }}</template>
            </span>
            <span class="dm-card__spacer"></span>
            <span v-if="preview.preview_limited" class="plan-pill plan-pill--free"><CygnusIcon name="lock" /> Preview</span>
          </div>

          <div class="dm-card__body dm-card__body--flush">
            <div v-if="error" class="dm-alert dm-alert--err">{{ error }}</div>

            <div class="dm-tbl-scroll" :class="{ 'dm-tbl-fade': preview.preview_limited }">
              <table class="dm-data">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Full postal address</th>
                    <th>Region</th>
                    <th>Staff</th>
                    <th>Registration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading"><td colspan="5" class="dm-empty-cell">Counting your audience…</td></tr>
                  <tr v-else-if="!preview.rows.length"><td colspan="5" class="dm-empty-cell">No businesses match these filters yet. Loosen a filter to widen the audience.</td></tr>
                  <tr v-for="row in preview.rows" :key="row.id" :class="{ 'dm-row-warn': !row.address_complete }">
                    <td class="td-name">
                      {{ row.business }}
                      <span v-if="row.sole_trader" class="td-muted">(sole trader)</span>
                    </td>
                    <td class="td-addr">
                      <template v-if="row.address">{{ row.address }}</template>
                      <div v-if="row.address_warning" class="addr-warn"><CygnusIcon name="warning" /> {{ row.address_warning }}</div>
                    </td>
                    <td>{{ row.region || '-' }}</td>
                    <td>{{ row.staff !== null ? row.staff : '-' }}</td>
                    <td><span class="m-pill" :class="regClass(row.registration_status)">{{ row.registration_label }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Soft-fade lock band (free tier) -->
            <div v-if="preview.preview_limited && preview.rows.length" class="dm-lock-band">
              <div class="dm-lock-band__ic"><CygnusIcon name="lock" /></div>
              <div class="dm-lock-band__title">Showing {{ fmtNum(preview.rows.length) }} of <b>{{ fmtNum(preview.total) }}</b> matches</div>
              <p class="dm-lock-band__sub">The full mailable list is ready. Unlock every address with a plan, or buy this one campaign as a one-off - print, postage and a per-record data fee, billed once.</p>
              <div class="dm-lock-band__cta">
                <router-link to="/admin/usage-plans" class="btn btn-violet"><CygnusIcon name="rocket" /> See plans</router-link>
                <button class="btn btn-violet-sec" @click="continueToDesign" title="Design this campaign, then buy it as a one-off">Buy this campaign <CygnusIcon name="arrow-right" /></button>
              </div>
            </div>
          </div>
        </section>

        <!-- Footer actions -->
        <div class="dm-action-bar">
          <span class="dm-action-bar__note">
            <template v-if="preview.preview_limited">Free plan shows the full match count and a {{ previewLimit }}-row preview. Targeting is never limited - only the export.</template>
            <template v-else-if="preview.excluded_incomplete > 0">{{ fmtNum(preview.excluded_incomplete) }} rows flagged for incomplete address - excluded from the mailable count above.</template>
            <template v-else>Ready to design your mail piece.</template>
          </span>
          <span class="dm-action-bar__spacer"></span>
          <button class="btn btn-sec" @click="openSaveModal"><CygnusIcon name="pin" /> {{ preview.preview_limited ? 'Save filters' : 'Save list' }}</button>
          <button class="btn btn-pri" @click="continueToDesign" title="Design your mail piece">Continue to design <CygnusIcon name="arrow-right" /></button>
        </div>
      </div>
    </div>

    <!-- ── Locked-feature rail (free tier) ── -->
    <div v-if="preview.preview_limited" class="dm-loop-rail">
      <div class="dm-loop-rail__head">
        <CygnusIcon name="rocket" />
        <h3>Turn one postcard into a tracked conversation</h3>
        <span class="plan-pill plan-pill--growth"><CygnusIcon name="lock" /> Growth plan</span>
      </div>
      <div class="dm-loop-grid">
        <div v-for="f in loopFeatures" :key="f.name" class="dm-loop-item">
          <div class="dm-loop-item__top">
            <CygnusIcon :name="f.icon" class="dm-loop-item__feat" />
            <span class="dm-loop-item__name">{{ f.name }}</span>
            <span class="dm-loop-item__lock"><CygnusIcon name="lock" /></span>
          </div>
          <div class="dm-loop-item__desc">{{ f.desc }}</div>
        </div>
      </div>
    </div>

    <!-- ── Save modal ── -->
    <div v-if="saveModalOpen" class="dm-modal-backdrop" @click.self="saveModalOpen = false">
      <div class="dm-modal">
        <h3 class="dm-modal__title"><CygnusIcon name="pin" /> Save this list</h3>
        <p class="dm-modal__sub">Save the current filters as a named audience you can reuse. We record a snapshot of the {{ fmtNum(preview.total) }} matching businesses at save time.</p>
        <input ref="saveNameInput" v-model="saveName" class="f-ctrl" type="text" placeholder="e.g. Cumbria trades, 1-9 staff" maxlength="120" @keyup.enter="saveList">
        <div v-if="saveError" class="dm-alert dm-alert--err">{{ saveError }}</div>
        <div class="dm-modal__actions">
          <button class="btn btn-sec" @click="saveModalOpen = false">Cancel</button>
          <button class="btn btn-pri" :disabled="saving || !saveName.trim()" @click="saveList"><CygnusIcon name="pin" /> {{ saving ? 'Saving…' : 'Save list' }}</button>
        </div>
      </div>
    </div>

    <transition name="dm-toast">
      <div v-if="message" class="dm-toast"><CygnusIcon name="check-circle" /> {{ message }}</div>
    </transition>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * DirectMailAudience — "Who gets mail" (Direct Mail slice DM-1).
 *
 * Faithful implementation of mockups/direct-mail/audience.html + audience-free.html:
 * a corpus filter panel, a debounced live-count chip, a recipient table with
 * address-quality warnings and registration badges, the highlighted sole-trader
 * include toggle, a save-list name modal, and the FREE-TIER state driven purely
 * by the API's `preview_limited` flag (the 25-row cap is enforced server-side).
 *
 * Free tier: the real match count is always shown; the list fades to a violet
 * lock band after the preview rows, and a loop-feature teaser rail appears. The
 * "Buy this campaign" CTA is a disabled DM-4 placeholder; "Continue to design"
 * is a disabled DM-2 placeholder. Copy uses plain hyphens (never em dashes).
 */
export default {
  name: "DirectMailAudience",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loaded: false,
      loading: false,
      error: null,
      message: "",
      messageTimer: null,
      debounceTimer: null,
      previewLimit: 25,
      isPaying: true,
      regionOptions: [],
      statusOptions: [
        { value: "registered", label: "Registered" },
        { value: "public_sector", label: "Public sector" },
        { value: "unregistered", label: "Unregistered" },
        { value: "unchecked", label: "Not yet checked" },
      ],
      staffBands: [
        { key: "1-4", min: 1, max: 4, on: false },
        { key: "5-9", min: 5, max: 9, on: false },
        { key: "10-49", min: 10, max: 49, on: false },
        { key: "50+", min: 50, max: 0, on: false },
      ],
      filters: {
        region: "",
        postcodeAreas: "",
        sectors: [],
        registrationStatuses: ["registered", "public_sector"],
        includeSoleTraders: true,
        completeOnly: true,
        hiring: false,
      },
      preview: {
        total: 0,
        matched_total: 0,
        excluded_incomplete: 0,
        suppressed_count: 0,
        rows: [],
        preview_limited: false,
      },
      // Live discovery (DM-5)
      discovering: false, // trigger request in flight
      discovery: { active: 0, queued: 0, running: 0, done: 0, in_progress: false },
      discoveryPollTimer: null,
      discoveryMsg: "",
      discoveryMsgType: "info", // info | warn | err
      savedList: [],
      loadedSavedId: null,
      saveModalOpen: false,
      saveName: "",
      saving: false,
      saveError: null,
      loopFeatures: [
        { icon: "window", name: "QR / PURL landing pages", desc: "A personal landing page and QR code per recipient." },
        { icon: "gauge", name: "Scan tracking", desc: "See who scanned the postcard and when, straight back in the lead record." },
        { icon: "envelope", name: "Email follow-up", desc: "A \"did you get our post?\" step you can drop into any drip flow, days after dispatch." },
        { icon: "bolt", name: "Day-zero new-business mail", desc: "Auto-post a welcome card the day a new business appears in your patch." },
      ],
    };
  },
  computed: {
    // Any direct_mail-origin discovery job still queued or running.
    discoveryActive() {
      return this.discovery.in_progress;
    },
  },
  created() {
    this.loadFilters();
    this.loadSaved();
    this.runPreview();
    // Resume the progress strip if a discovery run is already in flight.
    this.resumeDiscovery();
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.messageTimer) clearTimeout(this.messageTimer);
    this.stopDiscoveryPolling();
  },
  methods: {
    async loadFilters() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/audience/filters");
        const body = res?.data ?? res;
        this.regionOptions = Array.isArray(body?.regions) ? body.regions : [];
        if (body?.preview_limit) this.previewLimit = Number(body.preview_limit);
      } catch (e) {
        /* filter vocab is a nicety - the panel still works without it */
      }
    },
    async loadSaved() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/audiences");
        const body = res?.data ?? res;
        this.savedList = Array.isArray(body?.audiences) ? body.audiences : [];
      } catch (e) {
        this.savedList = [];
      }
    },
    schedulePreview() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.runPreview(), 350);
    },
    filterPayload() {
      let min = 0;
      let max = 0;
      const on = this.staffBands.filter((b) => b.on);
      if (on.length) {
        min = Math.min(...on.map((b) => b.min));
        max = on.some((b) => b.max === 0) ? 0 : Math.max(...on.map((b) => b.max));
      }
      return {
        region: this.filters.region,
        postcode_areas: this.filters.postcodeAreas,
        sectors: this.filters.sectors,
        staff_min: min,
        staff_max: max,
        registration_status: this.filters.registrationStatuses,
        include_sole_traders: this.filters.includeSoleTraders,
        complete_address_only: this.filters.completeOnly,
        hiring: this.filters.hiring,
      };
    },
    async runPreview() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.post("/api/v1/direct-mail/audience/preview", {
          filters: this.filterPayload(),
          page: 1,
          per_page: 100,
        });
        const body = res?.data ?? res;
        this.preview = {
          total: Number(body?.total) || 0,
          matched_total: Number(body?.matched_total) || 0,
          excluded_incomplete: Number(body?.excluded_incomplete) || 0,
          suppressed_count: Number(body?.suppressed_count) || 0,
          rows: Array.isArray(body?.rows) ? body.rows : [],
          preview_limited: !!body?.preview_limited,
        };
        this.isPaying = !!body?.is_paying;
        this.loaded = true;
      } catch (e) {
        this.error = this.errText(e, "Could not load your audience.");
      } finally {
        this.loading = false;
      }
    },
    // ── live discovery (DM-5) ──
    async findMore() {
      if (this.discovering || this.discoveryActive) return;
      this.discovering = true;
      this.discoveryMsg = "";
      try {
        const res = await this.api.post("/api/v1/direct-mail/audience/discover", { filters: this.filterPayload() });
        const body = res?.data ?? res;
        const n = Number(body?.jobs) || 0;
        const where = Array.isArray(body?.targets) && body.targets.length ? body.targets.join(", ") : "your area";
        this.discoveryMsg = "Started " + n + " search" + (n === 1 ? "" : "es") + " around " + where + ".";
        this.discoveryMsgType = "info";
        this.startDiscoveryPolling();
      } catch (e) {
        this.handleDiscoverError(e);
      } finally {
        this.discovering = false;
      }
    },
    handleDiscoverError(e) {
      const st = e?.response?.status ?? e?.status;
      const body = e?.response?.data ?? e?.data ?? {};
      if (st === 402 || body?.requires_plan) {
        this.discoveryMsg = "Live discovery is a paid feature. Upgrade to grow the pool automatically.";
        this.discoveryMsgType = "warn";
      } else if (st === 422) {
        this.discoveryMsg = body?.message || "Add a region or one or more postcode areas so we know where to search.";
        this.discoveryMsgType = "warn";
      } else if (st === 429) {
        const secs = Number(body?.retry_after) || 0;
        const mins = Math.max(1, Math.ceil(secs / 60));
        this.discoveryMsg = "You just started a discovery run. Give it a few minutes, then try again in about " + mins + " minute" + (mins === 1 ? "" : "s") + ".";
        this.discoveryMsgType = "warn";
      } else {
        this.discoveryMsg = this.errText(e, "Could not start discovery. Please try again.");
        this.discoveryMsgType = "err";
      }
    },
    startDiscoveryPolling() {
      this.stopDiscoveryPolling();
      this.pollDiscovery();
      this.discoveryPollTimer = setInterval(() => this.pollDiscovery(), 20000);
    },
    stopDiscoveryPolling() {
      if (this.discoveryPollTimer) {
        clearInterval(this.discoveryPollTimer);
        this.discoveryPollTimer = null;
      }
    },
    async pollDiscovery() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/audience/discover/status");
        const body = res?.data ?? res;
        this.discovery = {
          active: Number(body?.active) || 0,
          queued: Number(body?.queued) || 0,
          running: Number(body?.running) || 0,
          done: Number(body?.done) || 0,
          in_progress: !!body?.in_progress,
        };
        // New leads may have landed - refresh the live count while jobs run.
        this.runPreview();
        if (!this.discovery.in_progress) this.stopDiscoveryPolling();
      } catch (e) {
        // Stop polling on a hard failure; the strip keeps its last state.
        this.stopDiscoveryPolling();
      }
    },
    // On mount, resume the strip + polling if a run is already active.
    async resumeDiscovery() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/audience/discover/status");
        const body = res?.data ?? res;
        this.discovery = {
          active: Number(body?.active) || 0,
          queued: Number(body?.queued) || 0,
          running: Number(body?.running) || 0,
          done: Number(body?.done) || 0,
          in_progress: !!body?.in_progress,
        };
        if (this.discovery.in_progress) this.startDiscoveryPolling();
      } catch (e) {
        /* no status - leave the strip hidden */
      }
    },
    toggleBand(b) {
      b.on = !b.on;
      this.schedulePreview();
    },
    toggleStatus(value) {
      const i = this.filters.registrationStatuses.indexOf(value);
      if (i === -1) this.filters.registrationStatuses.push(value);
      else this.filters.registrationStatuses.splice(i, 1);
      this.schedulePreview();
    },
    // ── saved audiences ──
    async onLoadSaved(id) {
      if (!id) return;
      try {
        const res = await this.api.get("/api/v1/direct-mail/audiences/" + id);
        const body = res?.data ?? res;
        const f = body?.audience?.filters || {};
        this.applyFilters(f);
        this.loadedSavedId = Number(id);
        this.runPreview();
        this.toast("Loaded saved list.");
      } catch (e) {
        this.toast(this.errText(e, "Could not load that saved list."));
      }
    },
    applyFilters(f) {
      this.filters.region = f.region || "";
      this.filters.postcodeAreas = Array.isArray(f.postcode_areas) ? f.postcode_areas.join(", ") : (f.postcode_areas || "");
      this.filters.sectors = Array.isArray(f.sectors) ? f.sectors : [];
      this.filters.registrationStatuses = Array.isArray(f.registration_status) ? f.registration_status : [];
      this.filters.includeSoleTraders = !!f.include_sole_traders;
      this.filters.completeOnly = f.complete_address_only !== false;
      this.filters.hiring = !!f.hiring;
      const min = Number(f.staff_min) || 0;
      const max = Number(f.staff_max) || 0;
      this.staffBands.forEach((b) => {
        b.on = (min > 0 || max > 0) && b.min >= min && (max === 0 || (b.max !== 0 && b.max <= max));
      });
    },
    async deleteSaved(id) {
      try {
        await this.api.delete("/api/v1/direct-mail/audiences/" + id);
        this.loadedSavedId = null;
        await this.loadSaved();
        this.toast("Saved list deleted.");
      } catch (e) {
        this.toast(this.errText(e, "Could not delete that saved list."));
      }
    },
    openSaveModal() {
      this.saveError = null;
      this.saveName = "";
      this.saveModalOpen = true;
      this.$nextTick(() => this.$refs.saveNameInput && this.$refs.saveNameInput.focus());
    },
    async saveList() {
      const name = this.saveName.trim();
      if (!name || this.saving) return;
      this.saving = true;
      this.saveError = null;
      try {
        await this.api.post("/api/v1/direct-mail/audiences", { name, filters: this.filterPayload() });
        this.saveModalOpen = false;
        await this.loadSaved();
        this.toast("Saved \"" + name + "\".");
      } catch (e) {
        this.saveError = this.errText(e, "Could not save this list.");
      } finally {
        this.saving = false;
      }
    },
    // ── continue to design (DM-2) ──
    continueToDesign() {
      // Carry the audience_id when a saved list is loaded so the builder can pull
      // a real sample record for the proof; otherwise the builder proofs against
      // a placeholder sample and the design's audience stays unset.
      const query = this.loadedSavedId ? { audience: this.loadedSavedId } : {};
      this.$router.push({ path: "/outreach/direct-mail/design", query });
    },
    // ── helpers ──
    regClass(status) {
      return {
        "m-pill--registered": status === "registered",
        "m-pill--public": status === "public_sector",
        "m-pill--unregistered": status === "unregistered",
        // Empty/NULL status = discovered but not yet Companies-House checked.
        "m-pill--unchecked": !status,
      };
    },
    fmtNum(n) {
      return (Number(n) || 0).toLocaleString();
    },
    toast(msg) {
      this.message = msg;
      if (this.messageTimer) clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => { this.message = ""; }, 3200);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.dm-page { max-width: 1240px; }
.text-muted { color: var(--m-text-secondary); }
.dm-head-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 820px; line-height: 1.55; font-size: 0.92rem; }
.dm-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: #fff; background: var(--m-accent, #4F46E5); border-radius: 999px; padding: 3px 8px; }
.plan-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; border-radius: 999px; padding: 3px 9px; }
.plan-pill--free { color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; }
.plan-pill--growth { color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; }

/* Stepper */
.dm-steps { display: flex; gap: 10px; margin: 18px 0 20px; flex-wrap: wrap; }
.dm-step { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border: 1px solid var(--m-border); border-radius: 12px; background: var(--m-bg-card); flex: 1; min-width: 200px; }
.dm-step.is-current { border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }
.dm-step.is-todo { opacity: 0.6; }
.dm-step__num { width: 26px; height: 26px; border-radius: 50%; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent-hover, #4338CA); font-weight: 700; font-size: 0.82rem; display: flex; align-items: center; justify-content: center; flex: none; }
.dm-step.is-current .dm-step__num { background: var(--m-accent); color: #fff; }
.dm-step__title { display: block; font-weight: 600; font-size: 0.86rem; color: var(--m-text-primary); }
.dm-step__sub { display: block; font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Grid */
.dm-grid { display: grid; grid-template-columns: 320px 1fr; gap: 20px; align-items: start; }
@media (max-width: 900px) { .dm-grid { grid-template-columns: 1fr; } }
.dm-results { min-width: 0; }

/* Cards */
.dm-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); }
.dm-card__head { display: flex; align-items: center; gap: 9px; padding: 14px 16px; border-bottom: 1px solid var(--m-border); }
.dm-card__icon { color: var(--m-accent); }
.dm-card__title { font-size: 0.92rem; font-weight: 700; margin: 0; }
.dm-card__subtitle { font-size: 0.76rem; color: var(--m-text-tertiary); }
.dm-card__spacer { flex: 1; }
.dm-card__body { padding: 16px; }
.dm-card__body--flush { padding: 0; }

/* Filters */
.filters { display: flex; flex-direction: column; gap: 16px; }
.f-row { display: flex; flex-direction: column; gap: 6px; }
.f-lbl { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-secondary); }
.f-ctrl { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 7px; font-size: 0.85rem; padding: 8px 10px; color: var(--m-text-primary); font-family: inherit; width: 100%; }
.f-note { font-size: 0.72rem; color: var(--m-text-tertiary); }
.dm-saved { display: flex; gap: 6px; align-items: center; }
.dm-saved .f-ctrl { flex: 1; }
.dm-icon-btn { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 7px; padding: 8px; color: var(--m-text-secondary); cursor: pointer; }
.dm-icon-btn:hover { color: #DC2626; border-color: #DC2626; }

.chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; padding: 5px 11px; border-radius: 999px; border: 1px solid var(--m-border); background: var(--m-bg-card); color: var(--m-text-secondary); cursor: pointer; user-select: none; }
.chip.is-on { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent); color: var(--m-accent-hover, #4338CA); }

/* Toggle card */
.toggle-card { display: flex; align-items: flex-start; gap: 12px; padding: 13px; border: 1px solid #E0D2FB; background: linear-gradient(180deg, #FAF7FF, var(--m-bg-card)); border-radius: 12px; cursor: pointer; }
.toggle-card__ic { color: #7C3AED; flex: none; margin-top: 2px; }
.toggle-card__body { flex: 1; }
.toggle-card__title { display: block; font-weight: 700; font-size: 0.85rem; color: var(--m-text-primary); }
.toggle-card__desc { display: block; font-size: 0.75rem; color: var(--m-text-secondary); line-height: 1.45; margin-top: 3px; }
.switch { position: relative; flex: none; width: 40px; height: 22px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; background: #CBD2DE; border-radius: 999px; transition: 0.2s; }
.slider::before { content: ""; position: absolute; width: 16px; height: 16px; left: 3px; top: 3px; background: #fff; border-radius: 50%; transition: 0.2s; }
.switch input:checked + .slider { background: #7C3AED; }
.switch input:checked + .slider::before { transform: translateX(18px); }

/* Preview chip */
.dm-preview { display: flex; align-items: center; gap: 12px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 18px; margin-bottom: 16px; flex-wrap: wrap; }
.dm-preview__ic { color: var(--m-accent); font-size: 1.2rem; }
.dm-preview__n { font-size: 1.7rem; font-weight: 750; color: var(--m-text-primary); }
.dm-preview__t { font-size: 0.85rem; color: var(--m-text-secondary); flex: 1; min-width: 160px; }
.dm-preview__excluded { font-size: 0.72rem; color: #9A5B12; text-align: right; line-height: 1.3; }
.dm-preview__supp { display: inline-flex; align-items: center; gap: 5px; font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Live discovery trigger */
.dm-discover-slot { flex-basis: 100%; display: flex; justify-content: flex-end; margin-top: 2px; }
.btn-discover { background: #7C3AED; border-color: #7C3AED; color: #fff; }
.btn-discover:hover:not(:disabled) { background: #6D28D9; }
.dm-discover-lock { display: inline-flex; align-items: center; gap: 8px; text-decoration: none; padding: 7px 12px; border-radius: 999px; border: 1px dashed #E0D2FB; background: linear-gradient(180deg, #FAF7FF, var(--m-bg-card)); color: #6D28D9; font-size: 0.8rem; font-weight: 600; }
.dm-discover-lock:hover { border-color: #C4B0F5; }
.dm-discover-lock__ic { display: inline-flex; width: 22px; height: 22px; border-radius: 50%; background: #F3EEFE; color: #7C3AED; align-items: center; justify-content: center; }

/* Spinning glyph while a discovery run is active */
.is-spin { animation: dm-spin 1.1s linear infinite; }
@keyframes dm-spin { to { transform: rotate(360deg); } }

/* Discovery status message */
.dm-discover-note { display: flex; align-items: center; gap: 8px; padding: 9px 13px; border-radius: 10px; font-size: 0.8rem; margin-bottom: 12px; }
.dm-discover-note--info { background: #EEFBF4; color: #0B6E4F; border: 1px solid #C7EBD8; }
.dm-discover-note--warn { background: #FAF7FF; color: #6D28D9; border: 1px solid #E0D2FB; }
.dm-discover-note--err { background: #FCECEC; color: #B42318; border: 1px solid #F5C9C4; }

/* Progress strip */
.dm-discover-strip { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; border: 1px solid #E0D2FB; background: linear-gradient(180deg, #FAF7FF, var(--m-bg-card)); }
.dm-discover-strip.is-done { border-color: #C7EBD8; background: linear-gradient(180deg, #EEFBF4, var(--m-bg-card)); }
.dm-discover-strip__ic { width: 34px; height: 34px; border-radius: 50%; background: #F3EEFE; color: #7C3AED; display: inline-flex; align-items: center; justify-content: center; flex: none; }
.dm-discover-strip.is-done .dm-discover-strip__ic { background: #DCF2E8; color: #0B6E4F; }
.dm-discover-strip__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.dm-discover-strip__title { font-weight: 700; font-size: 0.86rem; color: var(--m-text-primary); }
.dm-discover-strip__sub { font-size: 0.76rem; color: var(--m-text-secondary); line-height: 1.4; }

/* Table */
.dm-tbl-scroll { overflow-x: auto; position: relative; }
.dm-tbl-fade::after { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 90px; background: linear-gradient(180deg, transparent, var(--m-bg-card)); pointer-events: none; }
.dm-data { width: 100%; border-collapse: collapse; }
.dm-data thead th { background: rgba(79, 70, 229, 0.06); border-bottom: 1px solid var(--m-border); text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--m-text-secondary); padding: 11px 14px; white-space: nowrap; }
.dm-data tbody td { padding: 0.7rem 0.85rem; font-size: 0.83rem; border-bottom: 1px solid var(--m-border); vertical-align: top; color: var(--m-text-primary); }
.dm-data tbody tr:last-child td { border-bottom: none; }
.dm-row-warn { background: #FFFAF2; }
.td-name { font-weight: 600; }
.td-muted { color: var(--m-text-tertiary); font-weight: 400; font-size: 0.78rem; }
.td-addr { white-space: pre-line; color: var(--m-text-secondary); }
.addr-warn { display: inline-flex; align-items: center; gap: 5px; margin-top: 4px; font-size: 0.72rem; color: #9A5B12; font-weight: 600; }
.dm-empty-cell { text-align: center; color: var(--m-text-tertiary); padding: 26px; font-size: 0.85rem; }
.m-pill { display: inline-block; font-size: 0.68rem; font-weight: 700; padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.m-pill--registered { background: #DCF2ED; color: #0B6E63; }
.m-pill--public { background: #E5EDFB; color: #2B4E9B; }
.m-pill--unregistered { background: #F3EEFE; color: #6D28D9; }
.m-pill--unchecked { background: #EEF1F5; color: #566072; }

/* Lock band */
.dm-lock-band { text-align: center; padding: 22px 18px 26px; border-top: 1px dashed #E0D2FB; background: linear-gradient(180deg, var(--m-bg-card), #FAF7FF); }
.dm-lock-band__ic { width: 42px; height: 42px; border-radius: 50%; background: #F3EEFE; color: #7C3AED; display: inline-flex; align-items: center; justify-content: center; font-size: 1.1rem; margin-bottom: 8px; }
.dm-lock-band__title { font-weight: 700; font-size: 1rem; color: var(--m-text-primary); }
.dm-lock-band__sub { max-width: 460px; margin: 6px auto 14px; font-size: 0.82rem; color: var(--m-text-secondary); line-height: 1.5; }
.dm-lock-band__cta { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

/* Action bar */
.dm-action-bar { display: flex; align-items: center; gap: 12px; margin-top: 16px; flex-wrap: wrap; }
.dm-action-bar__note { font-size: 0.78rem; color: var(--m-text-tertiary); }
.dm-action-bar__spacer { flex: 1; }

/* Buttons */
.btn { font-family: inherit; font-weight: 600; font-size: 0.82rem; padding: 0.5rem 0.95rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-pri { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.btn-pri:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-sec { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.btn-sec:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.btn-violet { background: #7C3AED; border-color: #7C3AED; color: #fff; }
.btn-violet:hover:not(:disabled) { background: #6D28D9; }
.btn-violet-sec { background: #fff; border-color: #E0D2FB; color: #6D28D9; }

/* Loop rail */
.dm-loop-rail { margin-top: 22px; border: 1px solid #E0D2FB; border-radius: 14px; background: linear-gradient(180deg, #FAF7FF, var(--m-bg-card)); padding: 18px; }
.dm-loop-rail__head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.dm-loop-rail__head h3 { font-size: 1rem; font-weight: 700; margin: 0; flex: 1; color: var(--m-text-primary); }
.dm-loop-rail__head > svg { color: #7C3AED; }
.dm-loop-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
@media (max-width: 1024px) { .dm-loop-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .dm-loop-grid { grid-template-columns: 1fr; } }
.dm-loop-item { border: 1px solid var(--m-border); border-radius: 12px; background: var(--m-bg-card); padding: 13px; }
.dm-loop-item__top { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; }
.dm-loop-item__feat { color: #7C3AED; }
.dm-loop-item__name { font-weight: 600; font-size: 0.82rem; color: var(--m-text-primary); flex: 1; }
.dm-loop-item__lock { color: var(--m-text-tertiary); }
.dm-loop-item__desc { font-size: 0.76rem; color: var(--m-text-secondary); line-height: 1.45; }

/* Alerts / modal / toast */
.dm-alert { padding: 9px 12px; font-size: 0.8rem; border-radius: 8px; margin: 12px; }
.dm-alert--err { background: #FCECEC; color: #B42318; border: 1px solid #F5C9C4; }
.dm-modal-backdrop { position: fixed; inset: 0; background: rgba(20, 22, 34, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.dm-modal { background: var(--m-bg-card); border-radius: 14px; padding: 22px; width: 100%; max-width: 440px; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25); }
.dm-modal__title { font-size: 1.05rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 8px; }
.dm-modal__sub { font-size: 0.82rem; color: var(--m-text-secondary); line-height: 1.5; margin: 0 0 14px; }
.dm-modal__actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
.dm-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: #0B6E63; color: #fff; padding: 10px 18px; border-radius: 999px; font-size: 0.84rem; font-weight: 600; display: flex; align-items: center; gap: 8px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); z-index: 1100; }
.dm-toast-enter-active, .dm-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.dm-toast-enter-from, .dm-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
</style>
